import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { auth } from '../firebase';

const TWOFA_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/twofa';

/**
 * First-time 2FA enrolment, inside the portal.
 *
 * Why this exists: the portal only ever rendered TwoFactorVerify, so a login with no
 * authenticator was asked for a code that did not exist anywhere and could never get
 * in. That is every GIDU seat holder accepting an invitation - they have a password
 * and a seat, and have never registered, so they have never been through enrolment.
 *
 * Deliberately NOT a copy of login-portal's version. That one also calls
 * provision-trial-public to start a SmartData trial, which is right for a new GIDO
 * owner registering their own organisation and wrong here: a seat holder works inside
 * someone else's organisation and must not be given a trial of their own.
 *
 * Only rendered when twofa(status) says enrolled: false. That guard matters - the
 * enroll action OVERWRITES twofa_secrets/{uid} with a fresh secret, so rendering this
 * for someone already enrolled would silently kill their working authenticator entry.
 * App.js asks the backend rather than inferring it.
 */
export default function TwoFactorSetup({ onComplete, onCancel }) {
  const [secret, setSecret] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const enroll = async () => {
      try {
        const idToken = await auth.currentUser.getIdToken();
        const res = await fetch(TWOFA_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
          body: JSON.stringify({ action: 'enroll' }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not start setup.');
        if (cancelled) return;
        setSecret(data.secret);
        setOtpauthUri(data.otpauth_uri);
      } catch (err) {
        console.error('2FA enroll failed:', err);
        if (!cancelled) setError(err.message || 'Could not start setup.');
      }
    };

    enroll();
    // The cancelled flag guards against React running this twice in development
    // (StrictMode double-invokes effects) - a second enroll would mint a second secret
    // and invalidate the QR code the person is already looking at.
    return () => { cancelled = true; };
  }, []);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(TWOFA_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ action: 'confirm', code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'That code was not accepted.');
      // confirm sets the twofa_verified claim; refresh so this session carries it.
      await auth.currentUser.getIdToken(true);
      onComplete();
    } catch (err) {
      console.error('2FA confirm failed:', err);
      setError(err.message || 'That code was not accepted.');
      setCode('');
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-logo">
          <span className="logo-d">D</span><span className="logo-bos">BOS</span>
        </div>
        <h1 className="login-title">Set up two-factor authentication</h1>
        <p className="login-subtitle">
          DBOS asks for a second factor every time you sign in. Scan this code with an
          authenticator app - Google Authenticator, 1Password, Authy or similar - then
          enter the six digits it shows. Nobody in your organisation ever sees this.
        </p>

        {error && <div className="error-message">{error}</div>}

        {otpauthUri ? (
          <div className="qr-code-section">
            <QRCodeSVG value={otpauthUri} size={200} level="M" includeMargin={true} />
            <div className="manual-entry">
              <p className="manual-label">Cannot scan? Enter this key by hand:</p>
              <code className="secret-code">{secret}</code>
            </div>
          </div>
        ) : (
          !error && <p className="login-subtitle">Preparing your code…</p>
        )}

        <form onSubmit={handleConfirm} className="login-form">
          <div className="input-group">
            <label htmlFor="code">Six-digit code</label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength="6"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
              disabled={loading || !otpauthUri}
              autoFocus
              className="code-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading || code.length !== 6 || !otpauthUri}
            className="login-button"
          >
            {loading ? 'Checking…' : 'Confirm and continue'}
          </button>
        </form>

        <button type="button" onClick={onCancel} className="link-button">
          Sign out
        </button>
      </div>
    </div>
  );
}
